import React, {FC, ReactNode, useEffect} from 'react';
import PaywallSectionPage from '~Umbraco/Pages/PaywallSectionPage/PaywallSectionPage';
import {
  ICollectionFragmentFragment,
  IPaywallFragmentFragment,
} from '~Api/Graphql/gql/types.generated';
import usePaywall from '@src/Hooks/usePaywall';
import useBreakpoint from 'design-system/src/WebComponent/Grid/hooks/useBreakpoint';
import {UmbTypeChecker} from '@src/Umbraco/Utils/UmbTypeChecker';
import { PaywallSectionTrackingProps, PlayerPaywallTrackingProps } from '@src/Services/Amplitude/hooks/tracker';

export type IPaywallParam = ICollectionFragmentFragment['paywall'];
export type IAccessTypParam = ICollectionFragmentFragment['accessType'];

interface PaywallCheckerProps {
  children: ReactNode;
  accessType?: IAccessTypParam;
  paywallId?: string;
  paywall?: IPaywallParam;
  render?: (children: ReactNode) => ReactNode;
  trackingData: PlayerPaywallTrackingProps | PaywallSectionTrackingProps;
}

/**
 * PaywallChecker is a guard component that conditionally renders either its children (protected content)
 * or a paywall page, based on the user's access rights and paywall preview settings.
 *
 * It checks if the user has access to the content (via accessType and paywall config),
 * and if not, displays the paywall (with optional preview if allowed).
 *
 * This component does not handle side effects or tracking logic; it is purely declarative and only
 * responsible for deciding what to render at the initial render time.
 *
 * @param {ReactNode} children - The protected content to render if access is granted.
 * @param {IAccessTypParam} [accessType] - The access type(s) required for the content.
 * @param {string} [paywallId] - Optional explicit paywall ID to use.
 * @param {IPaywallParam} [paywall] - The paywall configuration object.
 * @param {(children: ReactNode) => ReactNode} [render] - Optional render prop for customizing paywall rendering.
 * @param {PlayerPaywallTrackingProps | PaywallSectionTrackingProps} trackingData - Tracking data for analytics (passed to paywall page).
 */
const PaywallChecker: FC<PaywallCheckerProps> = ({
  children,
  accessType,
  paywall,
  paywallId: defaultPaywallId,
  render,
  trackingData,
}) => {
  const {canSeeContent, getPremiumPreviewSettings} = usePaywall();
  const {isMobile} = useBreakpoint();

  const paywallId: string | undefined =
    defaultPaywallId ??
    (UmbTypeChecker.isPaywall(paywall) ? paywall.id : undefined);

  const hasAccess: boolean = accessType ? canSeeContent(accessType) : true;
  const premiumPreviewSettings = getPremiumPreviewSettings(paywall as IPaywallFragmentFragment);
  const canPreview = premiumPreviewSettings?.canPreview;
  const shouldDisplayPaywall = !hasAccess && !canPreview;

  const paywallElement = (
    <PaywallSectionPage
      id={paywallId}
      orientation={isMobile ? 'portrait' : 'landscape'}
      trackingData={trackingData}
    />
  );

  return (
    <>
      {shouldDisplayPaywall
        ? render
          ? render(paywallElement)
          : paywallElement
        : children}
    </>
  );
};

export default PaywallChecker;
